import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, MapPin, Users, ArrowRight, Bell, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkshopsPage() {
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyName, setNotifyName] = useState('');
  const [submittingNotify, setSubmittingNotify] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchWorkshops();
  }, []);

  const fetchWorkshops = async () => {
    try {
      const response = await axios.get(`${API}/workshops`);
      setWorkshops(response.data);
    } catch (error) {
      console.error('Failed to fetch workshops:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotifySubmit = async (e) => {
    e.preventDefault();
    if (!notifyEmail) {
      toast.error('Please enter your email');
      return;
    }
    
    setSubmittingNotify(true);
    try {
      const response = await axios.post(`${API}/workshops/notify`, {
        email: notifyEmail,
        name: notifyName
      });
      toast.success(response.data.message);
      setNotifyEmail('');
      setNotifyName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign up for notifications');
    } finally {
      setSubmittingNotify(false);
    }
  };

  return (
    <div className="min-h-screen bg-artsy-background">
      {/* Hero */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-pink-500 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading text-5xl md:text-6xl font-bold mb-6">
            Holiday Workshops
          </h1>
          <p className="font-body text-xl text-pink-100 max-w-2xl mx-auto">
            Fun-filled art workshops during school holidays. Perfect for exploring new techniques and creating amazing artwork!
          </p>
        </div>
      </section>

      {/* Workshops Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-4xl font-bold text-slate-900 mb-8">
            Upcoming <span className="text-pink-500">Workshops</span>
          </h2>

          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : workshops.length === 0 ? (
            <div className="card-sticker text-center py-12">
              <h3 className="font-heading text-2xl font-bold text-slate-900 mb-4">No Workshops Scheduled</h3>
              <p className="font-body text-slate-600 mb-6">
                Check back soon for upcoming holiday workshops! We'll have exciting art activities planned.
              </p>
              <Link to="/contact" className="btn-secondary" data-testid="workshop-contact">
                Get Notified
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {workshops.map((workshop) => (
                <div key={workshop.id} className="card-sticker group" data-testid={`workshop-card-${workshop.id}`}>
                  {workshop.image_url && (
                    <div className="relative mb-4 overflow-hidden rounded-2xl border-2 border-slate-900">
                      <img 
                        src={workshop.image_url}
                        alt={workshop.name}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {workshop.is_full && (
                        <div className="absolute top-2 right-2">
                          <span className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full border-2 border-slate-900">
                            FULL
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">{workshop.name}</h3>
                  <p className="font-body text-slate-600 mb-4 line-clamp-2">{workshop.description}</p>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-slate-700 font-body text-sm">
                      <Calendar className="w-4 h-4 text-pink-500" />
                      <span>{workshop.date}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-700 font-body text-sm">
                      <Clock className="w-4 h-4 text-pink-500" />
                      <span>{workshop.time} ({workshop.duration})</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-700 font-body text-sm">
                      <MapPin className="w-4 h-4 text-pink-500" />
                      <span>{workshop.location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-700 font-body text-sm">
                      <Users className="w-4 h-4 text-pink-500" />
                      <span>{workshop.enrolled_count}/{workshop.max_participants} spots filled</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                    <span className="font-heading text-2xl font-bold text-pink-500">£{workshop.price}</span>
                    {user ? (
                      workshop.is_full ? (
                        <span className="font-body text-red-500 font-semibold">Full</span>
                      ) : (
                        <Link 
                          to="/portal" 
                          className="btn-secondary text-sm py-2 px-4"
                          data-testid={`book-workshop-${workshop.id}`}
                        >
                          Book Now
                        </Link>
                      )
                    ) : (
                      <Link 
                        to="/register" 
                        className="btn-secondary text-sm py-2 px-4"
                        data-testid={`register-workshop-${workshop.id}`}
                      >
                        Register
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-4xl font-bold text-slate-900 text-center mb-12">
            What to <span className="text-teal-600">Expect</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="card-sticker text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-slate-900 shadow-sticker">
                <span className="text-2xl">🎨</span>
              </div>
              <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">Fun Projects</h3>
              <p className="font-body text-slate-600">Themed art activities perfect for the holiday season.</p>
            </div>
            <div className="card-sticker text-center">
              <div className="w-16 h-16 bg-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-slate-900 shadow-sticker">
                <span className="text-2xl">✨</span>
              </div>
              <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">All Materials</h3>
              <p className="font-body text-slate-600">Everything needed is provided - just bring enthusiasm!</p>
            </div>
            <div className="card-sticker text-center">
              <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-slate-900 shadow-sticker">
                <span className="text-2xl">🏆</span>
              </div>
              <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">Take Home Art</h3>
              <p className="font-body text-slate-600">Children take home their amazing creations.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Notify Me CTA */}
      <section className="py-16 bg-yellow-400">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full border-2 border-slate-900 shadow-sticker mb-4">
              <Bell className="w-8 h-8 text-pink-500" />
            </div>
            <h2 className="font-heading text-4xl font-bold text-slate-900 mb-4">
              Want to be notified about new workshops?
            </h2>
            <p className="font-body text-lg text-slate-700">
              Leave your email and we'll let you know when new workshops are announced!
            </p>
          </div>
          
          <form onSubmit={handleNotifySubmit} className="bg-white rounded-2xl border-2 border-slate-900 shadow-sticker p-6">
            <div className="space-y-4">
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">Your Name (optional)</label>
                <input
                  type="text"
                  value={notifyName}
                  onChange={(e) => setNotifyName(e.target.value)}
                  placeholder="Your name"
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl font-body focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  data-testid="notify-name"
                />
              </div>
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">Email Address *</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-xl font-body focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    data-testid="notify-email"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={submittingNotify}
                className="w-full btn-primary flex items-center justify-center gap-2"
                data-testid="notify-submit"
              >
                {submittingNotify ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <Bell className="w-5 h-5" /> Notify Me
                  </>
                )}
              </button>
            </div>
          </form>
          
          <p className="text-center font-body text-sm text-slate-700 mt-4">
            Or <Link to="/register" className="text-blue-600 hover:underline font-semibold">create an account</Link> to enroll directly when workshops are available.
          </p>
        </div>
      </section>
    </div>
  );
}
